import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import clsx from "clsx";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  EyeIcon,
  MagnifyingGlassIcon,
  PencilSquareIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faHospital,
  faSyncAlt,
  faSlidersH,
} from "@fortawesome/free-solid-svg-icons";
import { BASE_URL } from "../../config";

const STATUS_MAP = {
  1: { label: "Active", className: "bg-emerald-100 text-emerald-700" },
  0: { label: "Inactive", className: "bg-rose-100 text-rose-700" },
  default: { label: "Unknown", className: "bg-gray-100 text-gray-700" },
};

const extractErrorMessage = (error, fallback) => {
  const message = error?.response?.data?.message;

  if (!message) return fallback;
  if (typeof message === "string") return message;

  if (typeof message === "object") {
    const firstValue = Object.values(message)[0];
    if (Array.isArray(firstValue)) return firstValue[0] || fallback;
    if (typeof firstValue === "string") return firstValue;
  }

  return fallback;
};

const ProviderClaimsSubmissionSettings = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [claimsSubmissionFrom, setClaimsSubmissionFrom] = useState("");
  const [claimsSubmissionTo, setClaimsSubmissionTo] = useState("");

  const [sortBy, setSortBy] = useState("provider_code");
  const [sortDirection, setSortDirection] = useState("asc");

  const [selectedIds, setSelectedIds] = useState([]);

  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewLoading, setViewLoading] = useState(false);
  const [viewRecord, setViewRecord] = useState(null);

  const [updateModalOpen, setUpdateModalOpen] = useState(false);
  const [updateSaving, setUpdateSaving] = useState(false);
  const [updateRecord, setUpdateRecord] = useState(null);
  const [updateClaimsSubmission, setUpdateClaimsSubmission] = useState("");

  const [bulkModalOpen, setBulkModalOpen] = useState(false);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [bulkClaimsSubmission, setBulkClaimsSubmission] = useState("");

  const getAuthHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem("authentication/token")}`,
  });

  const totalPages = Math.max(1, Math.ceil(totalCount / itemsPerPage));

  const currentPageIds = useMemo(
    () =>
      rows
        .map((row) => Number(row.tbl_id))
        .filter((id) => Number.isFinite(id) && id > 0),
    [rows]
  );

  const isAllCurrentPageSelected =
    currentPageIds.length > 0 &&
    currentPageIds.every((id) => selectedIds.includes(id));

  const getPageNumbers = () => {
    const maxPagesToShow = 5;
    const startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    const endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
    return Array.from(
      { length: endPage - startPage + 1 },
      (_, index) => startPage + index
    );
  };

  const fetchSettings = useCallback(async () => {
    setLoading(true);

    try {
      const response = await axios.get(
        `${BASE_URL}/providers/settings/claims-submission`,
        {
          headers: getAuthHeaders(),
          params: {
            limit_offset: (currentPage - 1) * itemsPerPage,
            limit: itemsPerPage,
            search_string: searchQuery.trim() || undefined,
            sort_by: sortBy,
            sort_direction: sortDirection,
            status_flag: statusFilter === "ALL" ? undefined : statusFilter,
            claims_submission_from:
              claimsSubmissionFrom === "" ? undefined : claimsSubmissionFrom,
            claims_submission_to:
              claimsSubmissionTo === "" ? undefined : claimsSubmissionTo,
          },
        }
      );

      setRows(response.data?.data || []);
      setTotalCount(Number(response.data?.total_count || 0));
    } catch (error) {
      toast.error(
        extractErrorMessage(
          error,
          "Failed to fetch provider submission of claims settings."
        )
      );
    } finally {
      setLoading(false);
    }
  }, [
    currentPage,
    itemsPerPage,
    searchQuery,
    sortBy,
    sortDirection,
    statusFilter,
    claimsSubmissionFrom,
    claimsSubmissionTo,
  ]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortDirection((previous) => (previous === "asc" ? "desc" : "asc"));
      return;
    }

    setSortBy(column);
    setSortDirection("asc");
    setCurrentPage(1);
  };

  const toggleSelectAllCurrentPage = () => {
    if (!currentPageIds.length) return;

    if (isAllCurrentPageSelected) {
      setSelectedIds((previous) =>
        previous.filter((id) => !currentPageIds.includes(id))
      );
      return;
    }

    setSelectedIds((previous) => {
      const next = new Set(previous);
      currentPageIds.forEach((id) => next.add(id));
      return Array.from(next);
    });
  };

  const toggleSelectOne = (tblId) => {
    const numericId = Number(tblId);
    if (!Number.isFinite(numericId) || numericId <= 0) return;

    setSelectedIds((previous) => {
      if (previous.includes(numericId)) {
        return previous.filter((id) => id !== numericId);
      }
      return [...previous, numericId];
    });
  };

  const handleOpenViewModal = async (providerCode) => {
    const encodedProviderCode = encodeURIComponent(providerCode);
    setViewModalOpen(true);
    setViewLoading(true);
    setViewRecord(null);

    try {
      const response = await axios.get(
        `${BASE_URL}/providers/settings/claims-submission/${encodedProviderCode}`,
        { headers: getAuthHeaders() }
      );
      setViewRecord(response.data?.data || null);
    } catch (error) {
      toast.error(
        extractErrorMessage(error, "Failed to load provider details.")
      );
      setViewModalOpen(false);
    } finally {
      setViewLoading(false);
    }
  };

  const handleOpenUpdateModal = (row) => {
    setUpdateRecord(row);
    setUpdateClaimsSubmission(String(row?.claims_submission ?? ""));
    setUpdateModalOpen(true);
  };

  const handleSubmitUpdate = async () => {
    if (!updateRecord?.provider_code) return;

    const numericValue = Number(updateClaimsSubmission);
    if (
      updateClaimsSubmission === "" ||
      !Number.isInteger(numericValue) ||
      numericValue < 0
    ) {
      toast.error("Submission of claims days must be a whole number of 0 or higher.");
      return;
    }

    setUpdateSaving(true);
    try {
      await axios.put(
        `${BASE_URL}/providers/settings/claims-submission/${encodeURIComponent(updateRecord.provider_code)}`,
        { claims_submission: numericValue },
        { headers: getAuthHeaders() }
      );

      toast.success("Provider setting updated successfully.");
      setUpdateModalOpen(false);
      setUpdateRecord(null);
      setUpdateClaimsSubmission("");
      fetchSettings();
    } catch (error) {
      toast.error(
        extractErrorMessage(error, "Failed to update provider setting.")
      );
    } finally {
      setUpdateSaving(false);
    }
  };

  const handleOpenBulkModal = () => {
    if (selectedIds.length === 0) {
      toast.info("Select at least one provider for bulk update.");
      return;
    }

    setBulkClaimsSubmission("");
    setBulkModalOpen(true);
  };

  const handleSubmitBulkUpdate = async () => {
    const numericValue = Number(bulkClaimsSubmission);
    if (
      bulkClaimsSubmission === "" ||
      !Number.isInteger(numericValue) ||
      numericValue < 0
    ) {
      toast.error("Submission of claims days must be a whole number of 0 or higher.");
      return;
    }

    setBulkSaving(true);
    try {
      const response = await axios.put(
        `${BASE_URL}/providers/settings/claims-submission/bulk-update`,
        {
          tbl_ids: selectedIds,
          claims_submission: numericValue,
        },
        { headers: getAuthHeaders() }
      );

      const updatedCount = response?.data?.updated_count ?? selectedIds.length;
      toast.success(`Bulk update completed. Updated ${updatedCount} record(s).`);
      setSelectedIds([]);
      setBulkModalOpen(false);
      fetchSettings();
    } catch (error) {
      toast.error(
        extractErrorMessage(error, "Failed to perform bulk update.")
      );
    } finally {
      setBulkSaving(false);
    }
  };

  const clearFilters = () => {
    setSearchQuery("");
    setStatusFilter("ALL");
    setClaimsSubmissionFrom("");
    setClaimsSubmissionTo("");
    setCurrentPage(1);
  };

  return (
    <div className="mx-auto w-full max-w-[1600px] p-4">
      <ToastContainer />

      <div className="mb-4 overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
        <div className="flex flex-col gap-3 bg-gradient-to-r from-cyan-600 via-blue-600 to-indigo-600 px-4 py-4 text-white md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-white/15 p-2 ring-1 ring-white/30">
              <FontAwesomeIcon icon={faHospital} className="h-5 w-5" />
            </div>
            <div>
              <h1 className="text-base font-semibold">
                Provider Submission of Claims (Contract) Days Settings
              </h1>
              <p className="text-xs text-white/90">
                {totalCount.toLocaleString()} total record
                {totalCount === 1 ? "" : "s"}
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={fetchSettings}
              className="inline-flex items-center gap-2 rounded-md bg-white/10 px-3 py-2 text-sm font-medium ring-1 ring-white/25 hover:bg-white/20"
            >
              <FontAwesomeIcon icon={faSyncAlt} />
              Refresh
            </button>

            <button
              type="button"
              onClick={clearFilters}
              className="inline-flex items-center gap-2 rounded-md bg-white/10 px-3 py-2 text-sm font-medium ring-1 ring-white/25 hover:bg-white/20"
            >
              Clear Filters
            </button>

            <button
              type="button"
              onClick={handleOpenBulkModal}
              className="inline-flex items-center gap-2 rounded-md bg-amber-300/90 px-3 py-2 text-sm font-semibold text-amber-900 hover:bg-amber-200"
            >
              <FontAwesomeIcon icon={faSlidersH} />
              Bulk Update ({selectedIds.length})
            </button>
          </div>
        </div>

        <div className="mx-4 mt-4 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          <p className="font-semibold">Important Note</p>
          <p className="mt-1">
            For batch submission detections, the system follows the AllAccess v1.0 logic and automatically applies a
            <span className="font-semibold"> +5 days grace period</span> on top of the configured contract days.
          </p>
          <p className="mt-1">
            Example: <span className="font-semibold">30 days</span> setting becomes an effective detection window of
            <span className="font-semibold"> 35 days</span>.
          </p>
          <p className="mt-1 text-xs text-amber-800">
            This settings module saves the base contract days only; the grace period is added during batch submission detection.
          </p>
        </div>

        <div className="grid gap-3 p-4 md:grid-cols-2 lg:grid-cols-5">
          <div className="relative lg:col-span-2">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(event) => {
                setSearchQuery(event.target.value);
                setCurrentPage(1);
              }}
              placeholder="Search provider code or name..."
              className="w-full rounded-md border border-gray-200 bg-white py-2 pl-9 pr-3 text-sm text-gray-700 placeholder:text-gray-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
            />
          </div>

          <select
            value={statusFilter}
            onChange={(event) => {
              setStatusFilter(event.target.value);
              setCurrentPage(1);
            }}
            className="rounded-md border border-gray-200 px-3 py-2 text-sm text-gray-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
          >
            <option value="ALL">All Statuses</option>
            <option value="1">Active</option>
            <option value="0">Inactive</option>
          </select>

          <input
            type="number"
            min="0"
            value={claimsSubmissionFrom}
            onChange={(event) => {
              setClaimsSubmissionFrom(event.target.value);
              setCurrentPage(1);
            }}
            placeholder="Days From"
            className="rounded-md border border-gray-200 px-3 py-2 text-sm text-gray-700 placeholder:text-gray-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
          />

          <input
            type="number"
            min="0"
            value={claimsSubmissionTo}
            onChange={(event) => {
              setClaimsSubmissionTo(event.target.value);
              setCurrentPage(1);
            }}
            placeholder="Days To"
            className="rounded-md border border-gray-200 px-3 py-2 text-sm text-gray-700 placeholder:text-gray-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
          />
        </div>
      </div>

      <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
        <div className="max-h-[62vh] overflow-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="sticky top-0 bg-gray-50">
              <tr>
                <th className="w-[44px] px-3 py-2 text-left">
                  <input
                    type="checkbox"
                    checked={isAllCurrentPageSelected}
                    onChange={toggleSelectAllCurrentPage}
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                </th>
                <th
                  onClick={() => handleSort("provider_code")}
                  className="cursor-pointer px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-gray-600"
                >
                  Provider Code{" "}
                  {sortBy === "provider_code" ? (sortDirection === "asc" ? "?" : "?") : ""}
                </th>
                <th
                  onClick={() => handleSort("provider_name")}
                  className="cursor-pointer px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-gray-600"
                >
                  Provider Name{" "}
                  {sortBy === "provider_name" ? (sortDirection === "asc" ? "?" : "?") : ""}
                </th>
                <th
                  onClick={() => handleSort("claims_submission")}
                  className="cursor-pointer px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-gray-600"
                >
                  Submission of Claims (Contract) Days{" "}
                  {sortBy === "claims_submission"
                    ? sortDirection === "asc"
                      ? "?"
                      : "?"
                    : ""}
                </th>
                <th
                  onClick={() => handleSort("status_flag")}
                  className="cursor-pointer px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-gray-600"
                >
                  Status {sortBy === "status_flag" ? (sortDirection === "asc" ? "?" : "?") : ""}
                </th>
                <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
                  Actions
                </th>
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-100">
              {loading
                ? Array.from({ length: itemsPerPage }).map((_, index) => (
                    <tr key={`skeleton-${index}`} className="animate-pulse">
                      {Array.from({ length: 6 }).map((__, cellIndex) => (
                        <td key={cellIndex} className="px-4 py-3">
                          <div className="h-4 rounded bg-gray-200" />
                        </td>
                      ))}
                    </tr>
                  ))
                : rows.length === 0
                ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-16 text-center text-sm text-gray-500">
                      No providers found for the selected filters.
                    </td>
                  </tr>
                  )
                : rows.map((row) => {
                    const rowId = Number(row.tbl_id);
                    const isSelected = selectedIds.includes(rowId);
                    const status = STATUS_MAP[row.status_flag] || STATUS_MAP.default;

                    return (
                      <tr
                        key={row.tbl_id}
                        className={clsx(
                          "transition-colors hover:bg-gray-50",
                          isSelected && "bg-blue-50/70"
                        )}
                      >
                        <td className="px-3 py-3">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleSelectOne(row.tbl_id)}
                            className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                        </td>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">
                          {row.provider_code || "N/A"}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-800">
                          {row.provider_name || "N/A"}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-800">
                          {row.claims_submission === null || row.claims_submission === ""
                            ? "N/A"
                            : `${row.claims_submission} Day(s)`}
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={clsx(
                              "inline-flex rounded-full px-2 py-0.5 text-xs font-medium",
                              status.className
                            )}
                          >
                            {status.label}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              title="View"
                              onClick={() => handleOpenViewModal(row.provider_code)}
                              className="rounded-md p-1.5 text-blue-600 hover:bg-blue-50 hover:text-blue-800"
                            >
                              <EyeIcon className="h-5 w-5" />
                            </button>
                            <button
                              type="button"
                              title="Update"
                              onClick={() => handleOpenUpdateModal(row)}
                              className="rounded-md p-1.5 text-amber-600 hover:bg-amber-50 hover:text-amber-800"
                            >
                              <PencilSquareIcon className="h-5 w-5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
            </tbody>
          </table>
        </div>

        <div className="flex flex-col items-center justify-between gap-3 border-t border-gray-100 p-3 text-sm text-gray-700 sm:flex-row">
          <div className="text-xs text-gray-600">
            Showing {totalCount ? (currentPage - 1) * itemsPerPage + 1 : 0} to{" "}
            {Math.min(currentPage * itemsPerPage, totalCount)} of{" "}
            {totalCount.toLocaleString()} entries
          </div>

          <div className="flex flex-wrap items-center gap-1">
            <button
              type="button"
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
              className={clsx(
                "flex items-center gap-1 rounded-md px-2 py-1 text-xs",
                currentPage === 1
                  ? "bg-gray-100 text-gray-400"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              )}
            >
              <ChevronDoubleLeftIcon className="h-5 w-5" />
              First
            </button>

            <button
              type="button"
              onClick={() => setCurrentPage((previous) => Math.max(1, previous - 1))}
              disabled={currentPage === 1}
              className={clsx(
                "flex items-center gap-1 rounded-md px-2 py-1 text-xs",
                currentPage === 1
                  ? "bg-gray-100 text-gray-400"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              )}
            >
              <ChevronLeftIcon className="h-5 w-5" />
              Previous
            </button>

            {getPageNumbers().map((page) => (
              <button
                key={page}
                type="button"
                onClick={() => setCurrentPage(page)}
                className={clsx(
                  "rounded-md px-2 py-1 text-xs",
                  currentPage === page
                    ? "bg-blue-600 text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                )}
              >
                {page}
              </button>
            ))}

            <button
              type="button"
              onClick={() =>
                setCurrentPage((previous) => Math.min(totalPages, previous + 1))
              }
              disabled={currentPage === totalPages}
              className={clsx(
                "flex items-center gap-1 rounded-md px-2 py-1 text-xs",
                currentPage === totalPages
                  ? "bg-gray-100 text-gray-400"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              )}
            >
              Next
              <ChevronRightIcon className="h-5 w-5" />
            </button>

            <button
              type="button"
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
              className={clsx(
                "flex items-center gap-1 rounded-md px-2 py-1 text-xs",
                currentPage === totalPages
                  ? "bg-gray-100 text-gray-400"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              )}
            >
              Last
              <ChevronDoubleRightIcon className="h-5 w-5" />
            </button>

            <select
              value={itemsPerPage}
              onChange={(event) => {
                setItemsPerPage(Number(event.target.value));
                setCurrentPage(1);
              }}
              className="ml-1 rounded-md border border-gray-200 px-2 py-1 text-xs focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
            >
              {[10, 20, 50, 100].map((size) => (
                <option key={size} value={size}>
                  {size} per page
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {viewModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-2xl">
            <div className="flex items-center justify-between bg-gradient-to-r from-cyan-600 to-blue-600 px-4 py-3 text-white">
              <h2 className="text-base font-semibold">
                View Provider Submission of Claims Setting
              </h2>
              <button
                type="button"
                onClick={() => setViewModalOpen(false)}
                className="text-white/90 hover:text-white"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-3 p-5">
              {viewLoading ? (
                <div className="space-y-2">
                  <div className="h-4 w-2/3 animate-pulse rounded bg-gray-200" />
                  <div className="h-4 w-1/2 animate-pulse rounded bg-gray-200" />
                  <div className="h-4 w-3/4 animate-pulse rounded bg-gray-200" />
                </div>
              ) : (
                <>
                  <div>
                    <p className="text-xs font-medium uppercase text-gray-500">Provider Code</p>
                    <p className="text-sm font-semibold text-gray-900">
                      {viewRecord?.provider_code || "N/A"}
                    </p>
                  </div>

                  <div>
                    <p className="text-xs font-medium uppercase text-gray-500">Provider Name</p>
                    <p className="text-sm text-gray-900">{viewRecord?.provider_name || "N/A"}</p>
                  </div>

                  <div>
                    <p className="text-xs font-medium uppercase text-gray-500">
                      Submission of Claims (Contract) Days
                    </p>
                    <p className="text-sm text-gray-900">
                      {viewRecord?.claims_submission === null ||
                      viewRecord?.claims_submission === ""
                        ? "N/A"
                        : `${viewRecord.claims_submission} Day(s)`}
                    </p>
                  </div>

                  <div>
                    <p className="text-xs font-medium uppercase text-gray-500">Status</p>
                    <p className="text-sm text-gray-900">
                      {(STATUS_MAP[viewRecord?.status_flag] || STATUS_MAP.default).label}
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {updateModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-2xl">
            <div className="flex items-center justify-between bg-gradient-to-r from-amber-600 to-orange-600 px-4 py-3 text-white">
              <h2 className="text-base font-semibold">
                Update Submission of Claims (Contract) Days
              </h2>
              <button
                type="button"
                onClick={() => setUpdateModalOpen(false)}
                className="text-white/90 hover:text-white"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4 p-5">
              <div className="rounded-lg border border-amber-100 bg-amber-50 p-3 text-xs text-amber-900">
                <div>
                  <span className="font-semibold">Provider:</span>{" "}
                  {updateRecord?.provider_name || "N/A"}
                </div>
                <div>
                  <span className="font-semibold">Code:</span>{" "}
                  {updateRecord?.provider_code || "N/A"}
                </div>
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  Submission of Claims (Contract) Days
                </label>
                <input
                  type="number"
                  min="0"
                  value={updateClaimsSubmission}
                  onChange={(event) => setUpdateClaimsSubmission(event.target.value)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-100"
                />
              </div>

              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setUpdateModalOpen(false)}
                  className="rounded-md border border-gray-200 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSubmitUpdate}
                  disabled={updateSaving}
                  className={clsx(
                    "rounded-md px-3 py-2 text-sm font-semibold text-white",
                    updateSaving
                      ? "cursor-not-allowed bg-amber-300"
                      : "bg-amber-600 hover:bg-amber-700"
                  )}
                >
                  {updateSaving ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {bulkModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-2xl">
            <div className="flex items-center justify-between bg-gradient-to-r from-indigo-600 to-blue-600 px-4 py-3 text-white">
              <h2 className="text-base font-semibold">Bulk Update Submission Days</h2>
              <button
                type="button"
                onClick={() => setBulkModalOpen(false)}
                className="text-white/90 hover:text-white"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4 p-5">
              <div className="rounded-lg border border-blue-100 bg-blue-50 p-3 text-sm text-blue-900">
                Selected providers: <span className="font-semibold">{selectedIds.length}</span>
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  New Submission of Claims (Contract) Days
                </label>
                <input
                  type="number"
                  min="0"
                  value={bulkClaimsSubmission}
                  onChange={(event) => setBulkClaimsSubmission(event.target.value)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                />
              </div>

              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setBulkModalOpen(false)}
                  className="rounded-md border border-gray-200 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSubmitBulkUpdate}
                  disabled={bulkSaving}
                  className={clsx(
                    "rounded-md px-3 py-2 text-sm font-semibold text-white",
                    bulkSaving
                      ? "cursor-not-allowed bg-blue-300"
                      : "bg-blue-600 hover:bg-blue-700"
                  )}
                >
                  {bulkSaving ? "Updating..." : "Apply Bulk Update"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProviderClaimsSubmissionSettings;





